import { useEffect, useMemo, useState } from "react";
import { Plus, Layers, Search, Check, ChevronRight, ChevronsDownUp, ChevronsUpDown, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import WorkAreaModal from "@/components/WorkAreaModal";

const PLACEMENT_META = {
  suelo: { label: "Suelos", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  pared: { label: "Paredes", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  techo: { label: "Techos", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  espacio: { label: "Espacios", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  organizacion: { label: "Organización", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};
const PLACEMENT_ORDER = ["suelo", "pared", "techo", "espacio", "organizacion"];

const byPreference = (a, b) => {
  const pa = a.preference || "", pb = b.preference || "";
  if (pa && pb) { const c = pa.localeCompare(pb, "es", { numeric: true, sensitivity: "base" }); if (c !== 0) return c; }
  else if (pa && !pb) return -1;
  else if (!pa && pb) return 1;
  return a.name.localeCompare(b.name, "es", { sensitivity: "base" });
};

export default function AreaPickerDialog({ open, onOpenChange, areas, levels, units, value, onSelect, onAreaCreated }) {
  const [search, setSearch] = useState("");
  const [placement, setPlacement] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [waOpen, setWaOpen] = useState(false);

  const selectedLevelId = useMemo(
    () => (areas || []).find((a) => a.area_id === value)?.level_id || null,
    [areas, value]
  );

  // Al abrir: todos colapsados salvo el nivel del área ya seleccionada.
  useEffect(() => {
    if (!open) return;
    setSearch("");
    setPlacement("");
    setExpanded(selectedLevelId ? new Set([selectedLevelId]) : new Set());
  }, [open, selectedLevelId]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (levels || [])
      .map((l) => ({
        level: l,
        items: (areas || [])
          .filter((a) => a.level_id === l.level_id)
          .filter((a) => !placement || a.placement === placement)
          .filter((a) => !q || a.name.toLowerCase().includes(q) || (PLACEMENT_META[a.placement]?.label || "").toLowerCase().includes(q))
          .sort(byPreference),
      }))
      .filter((g) => g.items.length > 0);
  }, [levels, areas, search, placement]);

  const filtering = Boolean(search.trim() || placement);
  const totalShown = useMemo(() => grouped.reduce((n, g) => n + g.items.length, 0), [grouped]);

  const placementCounts = useMemo(() => {
    const m = {};
    (areas || []).forEach((a) => { if (a.placement) m[a.placement] = (m[a.placement] || 0) + 1; });
    return m;
  }, [areas]);

  const toggle = (id) => setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const expandAll = () => setExpanded(new Set(grouped.map((g) => g.level.level_id)));
  const collapseAll = () => setExpanded(new Set());

  const pick = (areaId) => { onSelect(areaId); onOpenChange(false); };

  const handleAreaCreated = (newArea, _wasEditing, unit) => {
    onAreaCreated?.(newArea, unit);
    setWaOpen(false);
    onSelect(newArea.area_id);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-none w-screen h-[100dvh] rounded-none p-0 gap-0 flex flex-col sm:w-[94vw] sm:h-[92vh] sm:max-w-[1400px] sm:rounded-xl overflow-hidden"
          data-testid="area-picker-dialog"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-border shrink-0 text-left space-y-3">
            <div>
              <DialogTitle className="flex items-center gap-2"><MapHeader /> Seleccionar área de trabajo</DialogTitle>
              <DialogDescription>Busca entre todas las áreas, filtradas por nivel y tipo de espacio.</DialogDescription>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9 h-11 text-base"
                placeholder="Buscar área por nombre o espacio…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                data-testid="area-picker-search"
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto pb-0.5 -mx-1 px-1">
              <FilterChip active={!placement} onClick={() => setPlacement("")} testId="area-picker-filter-all">
                Todos <span className="opacity-60">· {areas.length}</span>
              </FilterChip>
              {PLACEMENT_ORDER.filter((p) => placementCounts[p]).map((p) => (
                <FilterChip key={p} active={placement === p} onClick={() => setPlacement(placement === p ? "" : p)} testId={`area-picker-filter-${p}`}>
                  {PLACEMENT_META[p].label} <span className="opacity-60">· {placementCounts[p]}</span>
                </FilterChip>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 rounded-full text-xs" onClick={expandAll} data-testid="area-picker-expand-all">
                  <ChevronsUpDown className="h-3.5 w-3.5" /> Expandir todo
                </Button>
                <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 rounded-full text-xs" onClick={collapseAll} data-testid="area-picker-collapse-all">
                  <ChevronsDownUp className="h-3.5 w-3.5" /> Colapsar todo
                </Button>
              </div>
              <Button type="button" size="sm" className="h-8 gap-1.5 rounded-full text-xs" onClick={() => setWaOpen(true)} data-testid="area-picker-new-area">
                <Plus className="h-3.5 w-3.5" /> Nueva área
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-3 py-3 bg-background-subtle/40" data-testid="area-picker-body">
            {grouped.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">
                No se encontró ningún área con esos criterios.{" "}
                <button type="button" className="text-primary font-medium underline" onClick={() => setWaOpen(true)} data-testid="area-picker-empty-new">Crear una nueva área</button>.
              </div>
            ) : grouped.map((g) => {
              const isOpen = filtering || expanded.has(g.level.level_id);
              return (
                <div key={g.level.level_id} className="mb-2 rounded-xl border border-border bg-card overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggle(g.level.level_id)}
                    className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left hover:bg-secondary/60 transition-colors"
                    data-testid={`area-picker-level-${g.level.level_id}`}
                  >
                    <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    <Layers className="h-4 w-4 text-primary shrink-0" />
                    <span className="font-semibold text-sm flex-1 truncate">{g.level.name}</span>
                    <span className="text-[11px] font-medium text-muted-foreground bg-secondary rounded-full px-2 py-0.5 shrink-0">{g.items.length}</span>
                  </button>
                  {isOpen && (
                    <div className="border-t border-border/70">
                      {g.items.map((a) => {
                        const pm = PLACEMENT_META[a.placement];
                        const sel = a.area_id === value;
                        return (
                          <button type="button" key={a.area_id} onClick={() => pick(a.area_id)} data-testid={`area-picker-opt-${a.area_id}`}
                            className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm border-b border-border/50 last:border-0 transition-colors ${sel ? "bg-primary/10" : "hover:bg-secondary/50"}`}>
                            <span className={`flex h-4 w-4 items-center justify-center rounded-full border shrink-0 ${sel ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"}`}>
                              {sel && <Check className="h-3 w-3" />}
                            </span>
                            {a.preference && <span className="text-[10px] text-muted-foreground font-mono shrink-0">#{a.preference}</span>}
                            <span className="truncate flex-1">{a.name}</span>
                            {pm && <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium shrink-0 ${pm.cls}`}>{pm.label}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="shrink-0 border-t border-border px-5 py-3 flex items-center justify-between gap-3 bg-card">
            <span className="text-xs text-muted-foreground" data-testid="area-picker-count">
              {totalShown} {totalShown === 1 ? "área" : "áreas"}{filtering ? " (filtradas)" : ""}
            </span>
            <Button type="button" variant="outline" className="rounded-full gap-1.5" onClick={() => onOpenChange(false)} data-testid="area-picker-close">
              <X className="h-4 w-4" /> Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <WorkAreaModal open={waOpen} onOpenChange={setWaOpen} levels={levels} units={units || []} defaultLevelId={placement ? null : null} area={null} onSaved={handleAreaCreated} />
    </>
  );
}

const MapHeader = () => <Search className="h-4 w-4 text-primary" />;

const FilterChip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground border-border hover:bg-secondary"}`}
  >
    {children}
  </button>
);
